import { useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { Badge, Button, Card, CardHeader, ConfirmDialog, EmptyState, ErrorState, Icon, IconButton, MenuDropdown, PageHeader, Skeleton, useToast } from '../../../components/ui';
import { useCrumb } from '../../../components/layout/Breadcrumbs/Breadcrumbs';
import { useAuth } from '../../../app/providers/AuthProvider';
import { useAction } from '../../../hooks/useAction';
import { useFetch } from '../../../hooks/useFetch';
import { cn } from '../../../utils/cn';
import { refreshMasters } from '../services/masterService';
import { MASTER_SECTIONS, RECORD_LINKS, sectionOf } from '../constants/masterSections';
import { masterService } from '../services/masterService';
import type { MasterEntry } from '../types/masterTypes';

function NavItem({ to, icon, title, active, count }: { to: string; icon: string; title: string; active?: boolean; count?: number | null }) {
  return (
    <Link
      to={to}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'flex shrink-0 items-center gap-2.5 rounded-lg px-3 py-2 text-sm outline-none transition-colors focus-visible:ring-2 focus-visible:ring-steel',
        active ? 'bg-brand-50 font-medium text-brand-700' : 'text-ink-600 hover:bg-navy-50',
      )}
    >
      <Icon name={icon} size={17} className={active ? 'text-brand-600' : 'text-ink-400'} />
      <span className="flex-1">{title}</span>
      {count != null && <span className="tabular text-xs text-ink-400">{count}</span>}
    </Link>
  );
}

/** Master data hub: editable dropdown lists, plus links to records that have their own screens. */
export default function MasterList() {
  const { type } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();
  const { busy, run } = useAction(toast);
  const [search, setSearch] = useState('');
  const [deleting, setDeleting] = useState<MasterEntry | null>(null);
  const section = sectionOf(type);
  useCrumb(`/masters/${type}`, section?.title);
  const { data: all, loading, error, reload } = useFetch(() => masterService.grouped(), []);
  const { data: rows, loading: rowsLoading, reload: reloadRows } = useFetch(
    () => (section && type ? masterService.listType(type) : Promise.resolve([])),
    [type],
  );

  if (!type) return <Navigate to={`/masters/${MASTER_SECTIONS[0].type}`} replace />;
  if (!section) return <Navigate to="/masters" replace />;

  const after = () => {
    reload();
    reloadRows();
    refreshMasters();
  };
  const toggle = async (m) => {
    if (await run(`t${m._id}`, () => masterService.update(m._id, { isActive: !m.isActive }), m.isActive ? `${m.label} hidden` : `${m.label} shown again`)) after();
  };
  const remove = async () => {
    if (!deleting) return;
    if (await run('delete', () => masterService.remove(deleting._id), `${deleting.label} deleted`)) after();
    setDeleting(null);
  };

  const list = (rows ?? []).filter((m) => !search || `${m.label} ${m.value} ${m.description ?? ''}`.toLowerCase().includes(search.toLowerCase()));
  const detail = (m) => (type === 'hsnSac' ? [m.description, m.gstRate != null && `GST ${m.gstRate}%`].filter(Boolean).join(' · ') : m.description);

  return (
    <>
      <PageHeader title="Master data" subtitle="Lists used in dropdowns across the app. Rename, reorder or hide entries any time." />
      <div className="grid items-start gap-5 lg:grid-cols-[260px_minmax(0,1fr)]">
        <div className="space-y-5">
          <Card className="p-2">
            <p className="px-3 pb-1 pt-2 text-[11px] font-medium uppercase tracking-[0.08em] text-ink-400">Lists</p>
            <nav aria-label="Master lists" className="flex gap-1 overflow-x-auto lg:flex-col">
              {MASTER_SECTIONS.map((s) => (
                <NavItem key={s.type} to={`/masters/${s.type}`} icon={s.icon} title={s.title} active={s.type === type} count={all?.[s.type]?.length} />
              ))}
            </nav>
          </Card>
          <Card className="hidden p-2 lg:block">
            <p className="px-3 pb-1 pt-2 text-[11px] font-medium uppercase tracking-[0.08em] text-ink-400">Records</p>
            <nav aria-label="Record screens" className="flex flex-col gap-1">
              {RECORD_LINKS.filter((l) => !l.owner || user?.role === 'owner').map((l) => (
                <NavItem key={l.to} {...l} />
              ))}
            </nav>
          </Card>
        </div>

        <Card className="min-w-0 overflow-hidden">
          <CardHeader
            title={section.title}
            subtitle={section.help}
            action={<Button size="sm" leftIcon="plus" onClick={() => navigate(`/masters/${type}/new`)}>Add</Button>}
            className="pb-4"
          />
          <div className="border-y border-line px-5 py-3">
            <div className="relative sm:w-72">
              <Icon name="search" size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={`Search ${section.title.toLowerCase()}`}
                aria-label={`Search ${section.title.toLowerCase()}`}
                className="h-9 w-full rounded-lg border border-line bg-white pl-9 pr-3 text-sm outline-none placeholder:text-ink-300 hover:border-navy-300 focus:border-brand-500 focus:ring-4 focus:ring-brand-500/15"
              />
            </div>
          </div>
          {error ? (
            <ErrorState error={error} onRetry={reload} />
          ) : (loading || rowsLoading) && !rows ? (
            <div className="space-y-2 p-5">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-10" />)}</div>
          ) : list.length === 0 ? (
            <EmptyState icon={section.icon} title={search ? 'Nothing matches' : `No ${section.title.toLowerCase()} yet`} action={!search && <Button leftIcon="plus" onClick={() => navigate(`/masters/${type}/new`)}>Add {section.single}</Button>} />
          ) : (
            <table className="w-full text-left text-sm">
              <thead className="bg-navy-50/40 text-[13px] text-ink-500">
                <tr>
                  <th scope="col" className="px-5 py-3 font-medium">Name</th>
                  <th scope="col" className="hidden px-5 py-3 font-medium md:table-cell">Details</th>
                  <th scope="col" className="hidden px-5 py-3 text-right font-medium sm:table-cell">Order</th>
                  <th scope="col" className="px-5 py-3 font-medium">Status</th>
                  <th scope="col" className="w-12 px-3"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {list.map((m) => (
                  <tr key={m._id} className={cn('hover:bg-navy-50/60', !m.isActive && 'text-ink-400')}>
                    <td className="px-5 py-3">
                      <Link to={`/masters/${type}/${m._id}/edit`} className={cn('font-medium hover:text-brand-600', m.isActive ? 'text-navy-900' : 'text-ink-400')}>{m.label}</Link>
                      {m.value !== m.label && <p className="font-mono text-xs text-ink-400">{m.value}</p>}
                    </td>
                    <td className="hidden px-5 py-3 text-ink-500 md:table-cell">{detail(m) || '-'}</td>
                    <td className="tabular hidden px-5 py-3 text-right text-ink-500 sm:table-cell">{m.sortOrder}</td>
                    <td className="px-5 py-3">
                      <span className="flex flex-wrap gap-1.5">
                        {m.isActive ? <Badge tone="green" dot>Active</Badge> : <Badge tone="grey" dot>Hidden</Badge>}
                        {m.isSystem && <Badge tone="blue">Built-in</Badge>}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <MenuDropdown
                        width={200}
                        trigger={<IconButton icon="moreH" size="sm" label={`Actions for ${m.label}`} loading={busy === `t${m._id}`} />}
                        items={[
                          { label: 'Edit', icon: 'edit', onClick: () => navigate(`/masters/${type}/${m._id}/edit`) },
                          { label: m.isActive ? 'Hide from dropdowns' : 'Show again', icon: m.isActive ? 'eyeOff' : 'eye', onClick: () => toggle(m) },
                          !m.isSystem && { divider: true },
                          !m.isSystem && { label: 'Delete', icon: 'trash', danger: true, onClick: () => setDeleting(m) },
                        ].filter(Boolean)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>
      <ConfirmDialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={remove}
        loading={busy === 'delete'}
        danger
        title={`Delete "${deleting?.label}"?`}
        message="Only entries that no record uses can be deleted. Hide it instead if it is in use."
        confirmLabel="Delete"
      />
    </>
  );
}
